import React, { useState, useEffect } from 'react';
import { useAuth } from '../../lib/auth-context';
import api from '../../lib/api';
import { 
  Package, 
  Truck, 
  Building2, 
  DollarSign,
  Plus,
  Search,
  MoreHorizontal,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Send,
  FileText,
  ClipboardList,
  Loader2,
  ArrowUpDown,
  ChevronRight,
  MapPin,
  Phone,
  Mail,
  Boxes
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';

export default function ProcurementPage() {
  const { currentProperty } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Dashboard data
  const [dashboard, setDashboard] = useState(null);
  
  // Data states
  const [vendors, setVendors] = useState([]);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [requisitions, setRequisitions] = useState([]);
  
  // Dialog states
  const [showVendorDialog, setShowVendorDialog] = useState(false);
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [showPODialog, setShowPODialog] = useState(false);
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  
  // Form states
  const [vendorForm, setVendorForm] = useState({
    vendor_code: '',
    name: '',
    vendor_type: 'general',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    address_city: '',
    payment_terms: 'net30'
  });
  
  const [itemForm, setItemForm] = useState({
    item_code: '',
    name: '',
    description: '',
    category_id: '',
    unit_of_measure: 'each',
    unit_cost: '',
    min_stock_level: 0,
    reorder_point: ''
  });
  
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    code: '',
    description: ''
  });
  
  const [locationForm, setLocationForm] = useState({
    name: '',
    code: '',
    location_type: 'store'
  });
  
  const [poForm, setPoForm] = useState({
    vendor_id: '',
    expected_delivery_date: '',
    payment_terms: 'net30',
    notes: '',
    lines: [{ item_id: '', quantity_ordered: 1, unit_price: 0 }]
  });

  useEffect(() => {
    if (currentProperty?.id) {
      loadAllData();
    }
  }, [currentProperty]);

  const loadAllData = async () => {
    try {
      setIsLoading(true);
      await Promise.all([
        fetchDashboard(),
        fetchVendors(),
        fetchItems(),
        fetchCategories(),
        fetchLocations(),
        fetchPurchaseOrders(),
        fetchRequisitions()
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDashboard = async () => {
    try {
      const response = await api.get(`/procurement/dashboard?property_id=${currentProperty.id}`);
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await api.get(`/procurement/vendors?property_id=${currentProperty.id}`);
      setVendors(response.data || []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await api.get(`/procurement/items?property_id=${currentProperty.id}`);
      setItems(response.data || []);
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get(`/procurement/categories?property_id=${currentProperty.id}`);
      setCategories(response.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await api.get(`/procurement/locations?property_id=${currentProperty.id}`);
      setLocations(response.data || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const fetchPurchaseOrders = async () => {
    try {
      const response = await api.get(`/procurement/purchase-orders?property_id=${currentProperty.id}`);
      setPurchaseOrders(response.data || []);
    } catch (error) {
      console.error('Error fetching purchase orders:', error);
    }
  };

  const fetchRequisitions = async () => {
    try {
      const response = await api.get(`/procurement/requisitions?property_id=${currentProperty.id}`);
      setRequisitions(response.data || []);
    } catch (error) {
      console.error('Error fetching requisitions:', error);
    }
  };

  const handleCreateVendor = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/procurement/vendors?property_id=${currentProperty.id}`, vendorForm);
      toast.success('Vendor created successfully');
      setShowVendorDialog(false);
      setVendorForm({
        vendor_code: '',
        name: '',
        vendor_type: 'general',
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        address_city: '',
        payment_terms: 'net30'
      });
      fetchVendors();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create vendor');
    }
  };

  const handleCreateItem = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/procurement/items?property_id=${currentProperty.id}`, {
        ...itemForm,
        unit_cost: itemForm.unit_cost ? parseFloat(itemForm.unit_cost) : null,
        reorder_point: itemForm.reorder_point ? parseFloat(itemForm.reorder_point) : null,
        category_id: itemForm.category_id || null
      });
      toast.success('Item created successfully');
      setShowItemDialog(false);
      setItemForm({
        item_code: '',
        name: '',
        description: '',
        category_id: '',
        unit_of_measure: 'each',
        unit_cost: '',
        min_stock_level: 0,
        reorder_point: ''
      });
      fetchItems();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create item');
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/procurement/categories?property_id=${currentProperty.id}`, categoryForm);
      toast.success('Category created successfully');
      setShowCategoryDialog(false);
      setCategoryForm({ name: '', code: '', description: '' });
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create category');
    }
  };

  const handleCreateLocation = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/procurement/locations?property_id=${currentProperty.id}`, locationForm);
      toast.success('Location created successfully');
      setShowLocationDialog(false);
      setLocationForm({ name: '', code: '', location_type: 'store' });
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create location');
    }
  };

  const handleCreatePO = async () => {
    try {
      if (!poForm.vendor_id || poForm.lines.length === 0 || !poForm.lines[0].item_id) {
        toast.error('Please select a vendor and add at least one item');
        return;
      }
      await api.post(`/procurement/purchase-orders?property_id=${currentProperty.id}`, {
        vendor_id: poForm.vendor_id,
        expected_delivery_date: poForm.expected_delivery_date || null,
        payment_terms: poForm.payment_terms,
        notes: poForm.notes || null,
        lines: poForm.lines.filter(l => l.item_id).map(l => ({
          item_id: l.item_id,
          quantity_ordered: parseFloat(l.quantity_ordered) || 1,
          unit_price: parseFloat(l.unit_price) || 0,
        }))
      });
      toast.success('Purchase order created');
      setShowPODialog(false);
      setPoForm({ vendor_id: '', expected_delivery_date: '', payment_terms: 'net30', notes: '', lines: [{ item_id: '', quantity_ordered: 1, unit_price: 0 }] });
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create PO');
    }
  };

  const handleApproveVendor = async (vendorId) => {
    try {
      await api.post(`/procurement/vendors/${vendorId}/approve`);
      toast.success('Vendor approved');
      fetchVendors();
    } catch (error) {
      toast.error('Failed to approve vendor');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: 'bg-slate-100 text-slate-800 dark:bg-slate-900 dark:text-slate-300', label: 'Draft' },
      submitted: { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300', label: 'Submitted' },
      approved: { color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300', label: 'Approved' },
      rejected: { color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300', label: 'Rejected' },
      sent: { color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300', label: 'Sent' },
      confirmed: { color: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-300', label: 'Confirmed' },
      partial_received: { color: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300', label: 'Partial' },
      received: { color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300', label: 'Received' },
      cancelled: { color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300', label: 'Cancelled' },
      pending: { color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300', label: 'Pending' },
      posted: { color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300', label: 'Posted' },
    };
    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', label: status };
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ET', {
      style: 'currency',
      currency: 'ETB',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  if (!currentProperty) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="procurement-no-property">
        <div className="text-center">
          <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium">No Property Selected</h3>
          <p className="text-muted-foreground">Please select a property to view procurement</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="procurement-loading">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="procurement-page">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Procurement & Inventory</h1>
          <p className="text-muted-foreground">Manage purchasing, inventory, and cost control</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid="add-category-btn">
                <Plus className="mr-2 h-4 w-4" />
                Category
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Category</DialogTitle>
                <DialogDescription>Create a new inventory category</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateCategory} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="cat-name">Name *</Label>
                  <Input
                    id="cat-name"
                    value={categoryForm.name}
                    onChange={(e) => setCategoryForm({...categoryForm, name: e.target.value})}
                    placeholder="Food & Beverage"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cat-code">Code</Label>
                  <Input
                    id="cat-code"
                    value={categoryForm.code}
                    onChange={(e) => setCategoryForm({...categoryForm, code: e.target.value})}
                    placeholder="FB"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setShowCategoryDialog(false)}>Cancel</Button>
                  <Button type="submit">Create</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          
          <Dialog open={showLocationDialog} onOpenChange={setShowLocationDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid="add-location-btn">
                <MapPin className="mr-2 h-4 w-4" />
                Location
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Storage Location</DialogTitle>
                <DialogDescription>Create a new storage location</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateLocation} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="loc-name">Name *</Label>
                  <Input
                    id="loc-name"
                    value={locationForm.name}
                    onChange={(e) => setLocationForm({...locationForm, name: e.target.value})}
                    placeholder="Main Store"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="loc-type">Type</Label>
                  <Select
                    value={locationForm.location_type}
                    onValueChange={(value) => setLocationForm({...locationForm, location_type: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="store">Store</SelectItem>
                      <SelectItem value="kitchen">Kitchen</SelectItem>
                      <SelectItem value="bar">Bar</SelectItem>
                      <SelectItem value="outlet">Outlet</SelectItem>
                      <SelectItem value="cold_storage">Cold Storage</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setShowLocationDialog(false)}>Cancel</Button>
                  <Button type="submit">Create</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="vendors" data-testid="tab-vendors">Vendors</TabsTrigger>
          <TabsTrigger value="purchase-orders" data-testid="tab-pos">Purchase Orders</TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* Stats Cards */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card data-testid="stat-vendors">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Active Vendors</CardTitle>
                <Truck className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dashboard?.vendor_count || 0}</div>
                <p className="text-xs text-muted-foreground">Approved suppliers</p>
              </CardContent>
            </Card>
            
            <Card data-testid="stat-items">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Inventory Items</CardTitle>
                <Package className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dashboard?.item_count || 0}</div>
                <p className="text-xs text-muted-foreground">Active SKUs</p>
              </CardContent>
            </Card>
            
            <Card data-testid="stat-low-stock">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Low Stock Items</CardTitle>
                <AlertTriangle className="h-4 w-4 text-amber-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-amber-600">{dashboard?.low_stock_count || 0}</div>
                <p className="text-xs text-muted-foreground">Needs reorder</p>
              </CardContent>
            </Card>
            
            <Card data-testid="stat-po-value">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Open PO Value</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatCurrency(dashboard?.purchase_orders?.total_value)}</div>
                <p className="text-xs text-muted-foreground">{dashboard?.purchase_orders?.pending || 0} pending</p>
              </CardContent>
            </Card>
          </div>

          {/* Low Stock Alerts */}
          {dashboard?.low_stock_items?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  Low Stock Alerts
                </CardTitle>
                <CardDescription>Items that need to be reordered</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboard.low_stock_items.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">{item.item_name}</p>
                        <p className="text-sm text-muted-foreground">
                          Current: {item.quantity} · Min: {item.min_level}
                        </p>
                      </div>
                      <Badge className="bg-amber-100 text-amber-800">Reorder</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick Stats */}
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Purchase Orders</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Pending Approval</span>
                    <Badge variant="outline">{dashboard?.purchase_orders?.pending || 0}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Confirmed</span>
                    <Badge variant="outline">{dashboard?.purchase_orders?.confirmed || 0}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Total POs</span>
                    <Badge variant="outline">{dashboard?.purchase_orders?.total || 0}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Requisitions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Pending Approval</span>
                    <Badge variant="outline">{dashboard?.requisitions?.pending_approval || 0}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Approved</span>
                    <Badge variant="outline">{dashboard?.requisitions?.approved || 0}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Inventory Tab */}
        <TabsContent value="inventory" className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Input placeholder="Search items..." className="w-64" />
            </div>
            <Dialog open={showItemDialog} onOpenChange={setShowItemDialog}>
              <DialogTrigger asChild>
                <Button data-testid="add-item-btn">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Item
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Add Inventory Item</DialogTitle>
                  <DialogDescription>Create a new item in your inventory</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateItem} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="item-code">Item Code *</Label>
                      <Input
                        id="item-code"
                        value={itemForm.item_code}
                        onChange={(e) => setItemForm({...itemForm, item_code: e.target.value})}
                        placeholder="ITEM001"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="item-uom">Unit of Measure</Label>
                      <Select
                        value={itemForm.unit_of_measure}
                        onValueChange={(value) => setItemForm({...itemForm, unit_of_measure: value})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="each">Each</SelectItem>
                          <SelectItem value="kg">Kilogram</SelectItem>
                          <SelectItem value="liter">Liter</SelectItem>
                          <SelectItem value="box">Box</SelectItem>
                          <SelectItem value="pack">Pack</SelectItem>
                          <SelectItem value="case">Case</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="item-name">Name *</Label>
                    <Input
                      id="item-name"
                      value={itemForm.name}
                      onChange={(e) => setItemForm({...itemForm, name: e.target.value})}
                      placeholder="Coffee Beans - Premium Blend"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="item-category">Category</Label>
                    <Select
                      value={itemForm.category_id}
                      onValueChange={(value) => setItemForm({...itemForm, category_id: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Category</SelectItem>
                        {categories.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="item-cost">Unit Cost (ETB)</Label>
                      <Input
                        id="item-cost"
                        type="number"
                        step="0.01"
                        value={itemForm.unit_cost}
                        onChange={(e) => setItemForm({...itemForm, unit_cost: e.target.value})}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="item-min">Minimum Stock</Label>
                      <Input
                        id="item-min"
                        type="number"
                        value={itemForm.min_stock_level}
                        onChange={(e) => setItemForm({...itemForm, min_stock_level: parseFloat(e.target.value) || 0})}
                        placeholder="0"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="item-desc">Description</Label>
                    <Textarea
                      id="item-desc"
                      value={itemForm.description}
                      onChange={(e) => setItemForm({...itemForm, description: e.target.value})}
                      placeholder="Item description..."
                      rows={2}
                    />
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setShowItemDialog(false)}>Cancel</Button>
                    <Button type="submit" data-testid="submit-item-btn">Create Item</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Inventory Items</CardTitle>
              <CardDescription>Manage your stock keeping units</CardDescription>
            </CardHeader>
            <CardContent>
              {items.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>UOM</TableHead>
                      <TableHead className="text-right">Unit Cost</TableHead>
                      <TableHead className="text-right">Min Stock</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item) => (
                      <TableRow key={item.id} data-testid={`item-${item.id}`}>
                        <TableCell className="font-mono">{item.item_code}</TableCell>
                        <TableCell className="font-medium">{item.name}</TableCell>
                        <TableCell>{item.inventory_categories?.name || '-'}</TableCell>
                        <TableCell className="capitalize">{item.unit_of_measure}</TableCell>
                        <TableCell className="text-right">{formatCurrency(item.unit_cost)}</TableCell>
                        <TableCell className="text-right">{item.min_stock_level}</TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem>View Details</DropdownMenuItem>
                              <DropdownMenuItem>Edit</DropdownMenuItem>
                              <DropdownMenuItem>Stock Movement</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No inventory items yet</p>
                  <Button variant="link" onClick={() => setShowItemDialog(true)} className="mt-2">
                    Add your first item
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Vendors Tab */}
        <TabsContent value="vendors" className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Input placeholder="Search vendors..." className="w-64" />
            </div>
            <Dialog open={showVendorDialog} onOpenChange={setShowVendorDialog}>
              <DialogTrigger asChild>
                <Button data-testid="add-vendor-btn">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Vendor
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Add Vendor</DialogTitle>
                  <DialogDescription>Register a new supplier</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateVendor} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="vendor-code">Vendor Code *</Label>
                      <Input
                        id="vendor-code"
                        value={vendorForm.vendor_code}
                        onChange={(e) => setVendorForm({...vendorForm, vendor_code: e.target.value})}
                        placeholder="VND001"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="vendor-type">Type</Label>
                      <Select
                        value={vendorForm.vendor_type}
                        onValueChange={(value) => setVendorForm({...vendorForm, vendor_type: value})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">General</SelectItem>
                          <SelectItem value="food">Food</SelectItem>
                          <SelectItem value="beverage">Beverage</SelectItem>
                          <SelectItem value="equipment">Equipment</SelectItem>
                          <SelectItem value="services">Services</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vendor-name">Company Name *</Label>
                    <Input
                      id="vendor-name"
                      value={vendorForm.name}
                      onChange={(e) => setVendorForm({...vendorForm, name: e.target.value})}
                      placeholder="ABC Supplies Ltd."
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="contact-name">Contact Person</Label>
                      <Input
                        id="contact-name"
                        value={vendorForm.contact_name}
                        onChange={(e) => setVendorForm({...vendorForm, contact_name: e.target.value})}
                        placeholder="John Doe"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact-phone">Phone</Label>
                      <Input
                        id="contact-phone"
                        value={vendorForm.contact_phone}
                        onChange={(e) => setVendorForm({...vendorForm, contact_phone: e.target.value})}
                        placeholder="+251 911 123 456"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-email">Email</Label>
                    <Input
                      id="contact-email"
                      type="email"
                      value={vendorForm.contact_email}
                      onChange={(e) => setVendorForm({...vendorForm, contact_email: e.target.value})}
                      placeholder="sales@supplier.com"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="vendor-city">City</Label>
                      <Input
                        id="vendor-city"
                        value={vendorForm.address_city}
                        onChange={(e) => setVendorForm({...vendorForm, address_city: e.target.value})}
                        placeholder="Addis Ababa"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="payment-terms">Payment Terms</Label>
                      <Select
                        value={vendorForm.payment_terms}
                        onValueChange={(value) => setVendorForm({...vendorForm, payment_terms: value})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cod">COD</SelectItem>
                          <SelectItem value="net15">Net 15</SelectItem>
                          <SelectItem value="net30">Net 30</SelectItem>
                          <SelectItem value="net60">Net 60</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setShowVendorDialog(false)}>Cancel</Button>
                    <Button type="submit" data-testid="submit-vendor-btn">Create Vendor</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Vendors</CardTitle>
              <CardDescription>Manage your suppliers and vendors</CardDescription>
            </CardHeader>
            <CardContent>
              {vendors.length > 0 ? (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {vendors.map((vendor) => (
                    <Card key={vendor.id} data-testid={`vendor-${vendor.id}`}>
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold">{vendor.name}</h3>
                              {vendor.is_approved ? (
                                <Badge className="bg-green-100 text-green-800">
                                  <CheckCircle2 className="h-3 w-3 mr-1" />
                                  Approved
                                </Badge>
                              ) : (
                                <Badge variant="outline">Pending</Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground font-mono">{vendor.vendor_code}</p>
                          </div>
                          <Badge variant="outline" className="capitalize">{vendor.vendor_type}</Badge>
                        </div>
                        <div className="mt-4 space-y-2 text-sm text-muted-foreground">
                          {vendor.contact_name && (
                            <p className="flex items-center gap-2">
                              <span>{vendor.contact_name}</span>
                            </p>
                          )}
                          {vendor.contact_phone && (
                            <p className="flex items-center gap-2">
                              <Phone className="h-3 w-3" />
                              {vendor.contact_phone}
                            </p>
                          )}
                          {vendor.contact_email && (
                            <p className="flex items-center gap-2">
                              <Mail className="h-3 w-3" />
                              {vendor.contact_email}
                            </p>
                          )}
                        </div>
                        <div className="mt-4 flex gap-2">
                          {!vendor.is_approved && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => handleApproveVendor(vendor.id)}
                            >
                              Approve
                            </Button>
                          )}
                          <Button variant="ghost" size="sm">View Details</Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Truck className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No vendors registered</p>
                  <Button variant="link" onClick={() => setShowVendorDialog(true)} className="mt-2">
                    Add your first vendor
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Purchase Orders Tab */}
        <TabsContent value="purchase-orders" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Purchase Orders</CardTitle>
                  <CardDescription>Manage purchase orders and receiving</CardDescription>
                </div>
                <Button onClick={() => setShowPODialog(true)} data-testid="create-po-btn">
                  <Plus className="mr-2 h-4 w-4" />
                  Create PO
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {purchaseOrders.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>PO Number</TableHead>
                      <TableHead>Vendor</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Order Date</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {purchaseOrders.map((po) => (
                      <TableRow key={po.id} data-testid={`po-${po.id}`}>
                        <TableCell className="font-mono">{po.po_number}</TableCell>
                        <TableCell>{po.vendors?.name || '-'}</TableCell>
                        <TableCell>{getStatusBadge(po.status)}</TableCell>
                        <TableCell>{new Date(po.order_date).toLocaleDateString()}</TableCell>
                        <TableCell className="text-right">{formatCurrency(po.total_amount)}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon">
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No purchase orders yet</p>
                  <p className="text-sm mt-1">Create a PO to order items from vendors</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create PO Dialog */}
      <Dialog open={showPODialog} onOpenChange={setShowPODialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Purchase Order</DialogTitle>
            <DialogDescription>Create a new purchase order for a vendor</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Vendor *</Label>
              <Select value={poForm.vendor_id} onValueChange={(v) => setPoForm({...poForm, vendor_id: v})}>
                <SelectTrigger data-testid="po-vendor-select">
                  <SelectValue placeholder="Select vendor" />
                </SelectTrigger>
                <SelectContent>
                  {vendors.map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Expected Delivery</Label>
                <Input type="date" value={poForm.expected_delivery_date} onChange={(e) => setPoForm({...poForm, expected_delivery_date: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Payment Terms</Label>
                <Select value={poForm.payment_terms} onValueChange={(v) => setPoForm({...poForm, payment_terms: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="net30">Net 30</SelectItem>
                    <SelectItem value="net60">Net 60</SelectItem>
                    <SelectItem value="cod">COD</SelectItem>
                    <SelectItem value="prepaid">Prepaid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Line Items</Label>
              {poForm.lines.map((line, idx) => (
                <div key={idx} className="grid grid-cols-3 gap-2">
                  <Select value={line.item_id} onValueChange={(v) => {
                    const newLines = [...poForm.lines];
                    const item = items.find(i => i.id === v);
                    newLines[idx] = {...newLines[idx], item_id: v, unit_price: item?.unit_cost || 0};
                    setPoForm({...poForm, lines: newLines});
                  }}>
                    <SelectTrigger><SelectValue placeholder="Select item" /></SelectTrigger>
                    <SelectContent>
                      {items.map(i => (
                        <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input type="number" min="1" placeholder="Qty" value={line.quantity_ordered} onChange={(e) => {
                    const newLines = [...poForm.lines];
                    newLines[idx] = {...newLines[idx], quantity_ordered: e.target.value};
                    setPoForm({...poForm, lines: newLines});
                  }} />
                  <Input type="number" placeholder="Price" value={line.unit_price} onChange={(e) => {
                    const newLines = [...poForm.lines];
                    newLines[idx] = {...newLines[idx], unit_price: e.target.value};
                    setPoForm({...poForm, lines: newLines});
                  }} />
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => setPoForm({...poForm, lines: [...poForm.lines, {item_id: '', quantity_ordered: 1, unit_price: 0}]})}>
                <Plus className="h-3 w-3 mr-1" /> Add Line
              </Button>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={poForm.notes} onChange={(e) => setPoForm({...poForm, notes: e.target.value})} placeholder="Additional notes..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPODialog(false)}>Cancel</Button>
            <Button onClick={handleCreatePO} data-testid="submit-po-btn">Create PO</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
